<template>
    <div class="ds-exchange-content">
        <VacanciesToolbar :active-filters-count="activeFiltersCount" @search-input="onSearch" @create="emit('create')" @change-view="onChangeView" @global-filter-click="toggleGlobalFilter" />
        <div v-if="isGlobalFilterOpen" class="relative">
            <div class="absolute right-0 top-2 z-20" v-click-outside="closeGlobalFilter">
                <VacanciesFilter
                    global
                    :departments-items="filterDepartmentsItems"
                    :statuses-items="filterStatusesItems"
                    :types-items="filterTypesItems"
                    :date-range="filterDate"
                    @update:departments-items="filterDepartmentsItems = $event"
                    @update:statuses-items="filterStatusesItems = $event"
                    @update:types-items="filterTypesItems = $event"
                    @update:date-range="filterDate = $event"
                    @check="onGlobalFilterCheck"
                    @reset="allFiltersReset"
                />
            </div>
        </div>
        <div v-if="gridView" class="pt-7">
            <div class="wrapper overflow-y-auto ds-exchange-grid">
                <template v-if="requests.length">
                    <div v-for="vacancy in requests" :key="vacancy.id" class="ds-exchange-grid-item">
                        <VacanciesGridItem :vacancy />
                    </div>
                </template>
                <div v-else class="col-span-full text-center pt-28 text-lg">Ничего не найдено</div>
            </div>
        </div>
        <!-- list view begin-->
        <div v-else>
            <div class="ds-exchange-table-header ds-exchange-list-grid pt-6 pb-1" >
                <div class="w-8"></div>
                <div class="ds-exchange-col-date flex relative">
                    <span class="cursor-pointer" @click="toggleOrdering">Дата и время</span>
                    <DateRangeFilter v-model="filterDate" @selected="onColumnFilterCheck" />
                </div>
                <div class="ds-exchange-col-position flex relative">Тип запроса
                    <icon class="relative top-0.5 ml-1 cursor-pointer" @click="toggleFilter('types')"><IconSort width="16" height="16" /></icon>
                    <VacanciesFilter v-if="openFilter === 'types'" v-model="filterTypesItems" width="240px" class="absolute top-5 z-10" v-click-outside="closeColumnFilter" @reset="onFilterTypesReset" />
                </div>
                <div class="ds-exchange-col-department flex relative">Подразделение
                    <icon class="relative top-0.5 ml-1 cursor-pointer" @click="toggleFilter('departments')"><IconSort width="16" height="16" /></icon>
                    <VacanciesFilter v-if="openFilter === 'departments'" v-model="filterDepartmentsItems" width="240px" class="absolute top-5 z-10" v-click-outside="closeColumnFilter" @check="onColumnFilterCheck" @reset="onFilterDepartmentReset" />
                </div>
                <div class="ds-exchange-col-status flex relative">Статус
                    <icon class="relative top-0.5 ml-1 cursor-pointer" @click="toggleFilter('statuses')"><IconSort width="16" height="16" /></icon>
                    <VacanciesFilter v-if="openFilter === 'statuses'" v-model="filterStatusesItems" width="180px" class="absolute top-5 z-10" v-click-outside="closeColumnFilter" @check="onColumnFilterCheck" @reset="onFilterStatusesReset" />
                </div>
                <div class="ds-exchange-col-employee">Сотрудник</div>
            </div>
            <div class="wrapper overflow-y-auto text-sm">
                <template v-if="requests.length">
                    <div v-for="request in requests" :key="request.id" class="ds-exchange-table-row ds-exchange-list-grid">
                        <RequestsListItem :request />
                    </div>
                </template>
                <div v-else class="text-center pt-28 text-lg">Ничего не найдено</div>
            </div>
        </div>
        <!-- list view end-->
        <Pager :items="exchangeStore.requestsCount" @page="onPage" />
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import RequestsListItem from './RequestsListItem.vue'
import VacanciesGridItem from './VacanciesGridItem.vue'
import Pager from './Pager.vue'
import VacanciesToolbar from './VacanciesToolbar.vue'
import VacanciesFilter from './VacanciesFilter.vue'
import DateRangeFilter from './DateRangeFilter.vue'
import IconSort from '@/ui/icons/IconSort.vue'
import dayjs from 'dayjs'
import type { Dayjs } from 'dayjs'
import debounce from 'lodash.debounce'
import { useExchangeStore } from '@/stores/exchangeStore'
import { useDepartmentStore } from '@/stores/departmentStore'
import { useEmploymentStore } from '@/stores/employmentStore'

const exchangeStore = useExchangeStore()
const departmentStore = useDepartmentStore()
const employmentStore = useEmploymentStore()

const emit = defineEmits(['user-click', 'check-click', 'create'])
const gridView = ref(false)
const searchText = ref('')
const filterTypesItems = ref<any[]>([])
const filterDepartmentsItems = ref<any[]>([])
const filterStatusesItems = ref<any[]>([])
const filterDate = ref<[Dayjs, Dayjs] | null>(null)
const requestsPerPage = ref(15)
const pageNum = ref(1)
const ordering = ref(true)
const isGlobalFilterOpen = ref(false)
const openFilter = ref<'types' | 'departments' | 'statuses' | null>(null)
const activeFiltersCount = computed(() => (
    filterTypesItems.value.filter(item => item.check).length
    + filterDepartmentsItems.value.filter(item => item.check).length
    + filterStatusesItems.value.filter(item => item.check).length
    + (filterDate.value ? 1 : 0)
))
const requests = computed(() => exchangeStore.requests)

const onPage = async (data:any) => {
    const { page, itemsPerPage } = data
    pageNum.value = page
    requestsPerPage.value = itemsPerPage.id
    await getRequests()
} 

const onSearch = debounce((text: string) => {
    searchText.value = text
    console.log(searchText.value)
    getRequests()
}, 500) 

const onChangeView = (grid: boolean) => {
    gridView.value = grid
}

const onFilterTypesReset = () => {
    filterTypesItems.value = [
        {check: false, name: 'Входящий запрос'},
        {check: false, name: 'Исходящий запрос'}
    ]
}

const onFilterDepartmentReset = () => {
    filterDepartmentsItems.value = departmentStore.myDepartments.map((item:any) => { return {check: false, ...item} } )
}

const onFilterStatusesReset = () => {
    filterStatusesItems.value = exchangeStore.requestStatuses.map((item:any) => { return {check: false, ...item} } )
}

const toggleOrdering = () => {
    ordering.value = !ordering.value
    getRequests()
}

const allFiltersReset = () => {
    onFilterTypesReset()
    filterDate.value = null
    onFilterDepartmentReset()
    onFilterStatusesReset()
    pageNum.value = 1
    getRequests()
}

const closeColumnFilter = () => { openFilter.value = null }
const toggleFilter = (filter: 'types' | 'departments' | 'statuses') => {
    openFilter.value = openFilter.value === filter ? null : filter
}
const toggleGlobalFilter = () => {
    closeColumnFilter()
    isGlobalFilterOpen.value = !isGlobalFilterOpen.value
}
const closeGlobalFilter = () => { isGlobalFilterOpen.value = false }
const onGlobalFilterCheck = () => {
    pageNum.value = 1
    getRequests()
}
const onColumnFilterCheck = () => {
    pageNum.value = 1
    getRequests()
}

async function getRequests() {
    const order = ordering.value ? 'dttm_work_start_at' : '-dttm_work_start_at'
    const data:any = {
        page_size: requestsPerPage.value,
        page: pageNum.value,
        ordering: order
    }
    if(filterDepartmentsItems.value.filter(item => item.check).length) data.department = filterDepartmentsItems.value.filter(item => item.check).map(item => item.id).join(',')
    if(filterStatusesItems.value.filter(item => item.check).length) data.status = filterStatusesItems.value.filter(item => item.check).map(item => item.value).join(',')
    if(filterDate.value) {
        data.dt_from = dayjs(filterDate.value[0]).format('YYYY-MM-DD')
        data.dt_to = dayjs(filterDate.value[1]).format('YYYY-MM-DD')
    }
    if(searchText.value) data.search = searchText.value
    await exchangeStore.fetchRequests(data)
}

onMounted(async () => {
    onFilterDepartmentReset()
    onFilterStatusesReset()
    onFilterTypesReset()
    await departmentStore.fetchDepartments()
    await employmentStore.fetchEmployments()
    await getRequests()
})
</script>

<style>
.wrapper {
    height: calc(100vh - 300px)
}
</style>
